import type { AVATAR_SYMBOLS } from '@secretario/shared';
import {
  applyBeardAction,
  applyOutfit,
  ACCESSORY_CHARACTERS,
  BEARDED_CHARACTERS,
  CLINIC_OUTFITS,
  beardLabel,
  beardModeOf,
  DRESSED_CHARACTERS,
  LOOK_PREF,
  OUTFIT_LABELS,
  OUTFITS,
  outfitIndex,
  parseLook,
  type Look,
  type Outfit,
  VOICE_CATALOG,
  voiceFor,
  voiceGenderOf,
  type BeardAction,
  type CharacterKey,
  HAIRSTYLES,
  HAIRSTYLE_CHARACTERS,
  HAIRSTYLE_LABELS,
  type Hairstyle,
} from '@secretario/shared';
import { lazy, Suspense, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useLocation } from 'wouter';
import {
  characterImage,
  characterTile,
  DEFAULT_CHARACTER,
  faceBackground,
  FAMILIES,
  findVariant,
  isOwnAvatar,
  isSymbolAvatar,
  FACE_FAMILY_IDS,
  LIVE_FAMILY_IDS,
  STYLE_GROUPS,
  TRACO_FAMILY_IDS,
  type Expression,
  type StyleGroupId,
} from '../lib/characters.js';
import { previewGlasses, previewHairstyle, previewOutfit } from '../lib/look.js';
import { useHasClinic } from '../lib/clinicGate.js';
import { useProfile, useUpdateProfile } from '../lib/useProfile.js';
import { OwnAvatarPanel } from '../components/avatar/OwnAvatarPanel.js';
import { stageFraming } from '../components/voice/characters3d.js';
import './ported/mudar-jeito.css';
import { BackIcon } from '../components/chrome/BackIcon.js';
import { cardMood, isLayeredCharacter } from '../components/voice/characters3d.js';
import { useDockingStage } from './useDockingStage.js';

const LiveStageLayers = lazy(() =>
  import('../components/voice/LiveStageLayers.js').then((module) => ({
    default: module.LiveStageLayers,
  })),
);
const NO_SOUND = () => ({ input: 0, output: 0, voice: null });

/** O que o personagem vivo "fala" (lábios em silêncio) ao ganhar cada personalidade. */
const TRAIT_SAY: Record<Expression, string> = {
  simpatico: 'Oi! Que bom te ver por aqui.',
  direto: 'Pode mandar. Eu resolvo.',
  divertido: 'Bora deixar o seu dia mais leve?',
  elegante: 'Será um prazer cuidar da sua agenda.',
  tranquilo: 'Com calma, a gente organiza tudo.',
  espontaneo: 'Opa! Já estou pronto pro próximo.',
};

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

// Cada personalidade tem o seu símbolo 3D (o mesmo dos avatares de símbolo).
const TRAITS: { id: Expression; symbol: (typeof AVATAR_SYMBOLS)[number]; label: string }[] = [
  { id: 'simpatico', symbol: 'sol', label: 'Simpático' },
  { id: 'direto', symbol: 'flecha', label: 'Direto' },
  { id: 'divertido', symbol: 'balao', label: 'Divertido' },
  { id: 'elegante', symbol: 'diamante', label: 'Elegante' },
  { id: 'tranquilo', symbol: 'lua', label: 'Tranquilo' },
  { id: 'espontaneo', symbol: 'raio', label: 'Espontâneo' },
];

/** As abas da escolha: os grupos de estilo e o "Crie o seu" (avatar a partir da selfie). */
type PageStyle = StyleGroupId | 'own';

/** Miniatura de cada grupo de estilo na aba. */
const STYLE_THUMB: Record<StyleGroupId, CharacterKey> = {
  live: 'gota',
  traco: 'traco-ela',
  faces: 'face-sol',
};

const DEFAULT_VARIANT = findVariant(DEFAULT_CHARACTER);
/** O personagem de uma família da escolha (cada família da escolha tem um só). */
function variantOf(familyId: string) {
  const variants = FAMILIES.find((f) => f.id === familyId)?.variants;
  return variants?.neutro ?? variants?.ela ?? variants?.ele ?? DEFAULT_VARIANT.variant;
}

const BEARD_CHOICES: { id: BeardAction; label: string }[] = [
  { id: 'deixar_crescer', label: 'Deixar crescer' },
  { id: 'so_sombra', label: 'Só a sombra' },
  { id: 'fazer', label: 'Fazer agora' },
  { id: 'manter_assim', label: 'Manter assim' },
];

/** "Mudar o jeito" e a primeira escolha (onboarding) são a mesma tela. */
export function MudarJeitoPage() {
  const [, navigate] = useLocation();
  const profileQ = useProfile();
  const update = useUpdateProfile();
  const onboarding = !profileQ.data?.onboardedAt;

  const [familyId, setFamilyId] = useState(DEFAULT_VARIANT.family.id);
  const [traits, setTraits] = useState<Expression[]>(['simpatico']);
  const [name, setName] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [saved, setSaved] = useState(false);
  // Estilo: Ao vivo 3D, Faces ou o avatar próprio.
  const [style, setStyle] = useState<PageStyle>('live');
  // Personagem salvo que saiu da escolha (3D Toy ou símbolo): continua valendo
  // até a pessoa escolher outro aqui. Nada é trocado sozinho.
  const [keptKey, setKeptKey] = useState<CharacterKey | null>(null);
  const [ownKey, setOwnKey] = useState<CharacterKey | null>(null);
  const [ownPreview, setOwnPreview] = useState<string | null>(null);
  // Voz da conversa ao vivo: escolhida aqui, entre as do gênero do personagem.
  const [voice, setVoice] = useState<string | null>(null);
  // Barba do Caio (visual vivo): o pedido vale ao salvar.
  const [beardAction, setBeardAction] = useState<BeardAction | null>(null);
  // Roupa: null = como está.
  const [outfitChoice, setOutfitChoice] = useState<Outfit | null>(null);
  const [nightChoice, setNightChoice] = useState<boolean | null>(null);
  // Óculos escuros na cabeça (Igor): null = como está.
  const [glassesChoice, setGlassesChoice] = useState<boolean | null>(null);
  // Penteado (nova personagem): null = como está.
  const [hairChoice, setHairChoice] = useState<Hairstyle | null>(null);
  const sample = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  // O palco do personagem acompanha a rolagem: encolhe e encaixa no topo.
  const { anchorRef, dockRef } = useDockingStage();

  useEffect(() => {
    if (hydrated || profileQ.isLoading) return;
    const p = profileQ.data;
    const current = p?.characterKey ?? DEFAULT_CHARACTER;
    const t = p?.traits ?? ['simpatico'];
    setTraits(t.length ? t : ['simpatico']);
    setVoice(typeof p?.preferences.voice === 'string' ? p.preferences.voice : null);
    if (isOwnAvatar(current)) {
      setStyle('own');
      setOwnKey(current);
      setName(p?.secretaryName?.trim() || 'Secretário');
      setHydrated(true);
      return;
    }
    const parsed = isSymbolAvatar(current) ? null : findVariant(current);
    const inLive = !!parsed && LIVE_FAMILY_IDS.includes(parsed.family.id);
    const inFaces = !!parsed && FACE_FAMILY_IDS.includes(parsed.family.id);
    const inTraco = !!parsed && TRACO_FAMILY_IDS.includes(parsed.family.id);
    if (parsed && (inLive || inFaces || inTraco)) {
      setFamilyId(parsed.family.id);
      setStyle(inFaces ? 'faces' : inTraco ? 'traco' : 'live');
      setName(p?.secretaryName?.trim() || parsed.variant.name);
    } else if (p?.onboardedAt) {
      // 3D Toy e símbolos saíram da escolha: a tela abre no Ao vivo 3D, mas o
      // personagem salvo segue valendo até a pessoa tocar em outro.
      setKeptKey(current);
      setName(p.secretaryName?.trim() || (parsed?.variant.name ?? 'Secretário'));
    } else {
      // Primeira escolha: começa pelo personagem padrão.
      setName(p?.secretaryName?.trim() || DEFAULT_VARIANT.variant.name);
    }
    setHydrated(true);
  }, [profileQ.data, profileQ.isLoading, hydrated]);

  const dominant: Expression = traits[traits.length - 1] ?? 'simpatico';
  const variant = variantOf(familyId);
  const isFaces = style === 'faces';
  const isOwn = style === 'own';
  // Vale o avatar próprio, o personagem antigo (enquanto não escolher outro) ou o tocado aqui.
  const chosenKey: CharacterKey = (isOwn ? ownKey : null) ?? keptKey ?? variant.key;
  const keptName = keptKey && !isSymbolAvatar(keptKey) ? findVariant(keptKey).variant.name : null;
  const defaultName = isOwn
    ? name.trim() || 'Secretário'
    : keptKey
      ? (keptName ?? 'Secretário')
      : variant.name;
  const voiceGender = voiceGenderOf(chosenKey);
  const voiceOptions = VOICE_CATALOG[voiceGender];
  const chosenVoice = voiceFor(chosenKey, voice);
  // No "Crie o seu" sem avatar ainda, o palco espera o desenho.
  const heroImg = isOwn
    ? (ownPreview ?? (ownKey ? characterImage(ownKey, dominant) : null))
    : characterImage(chosenKey, dominant);
  // Ao vivo 3D e Faces: o palco é o personagem vivo, que reage à personalidade escolhida.
  const liveHero = !isOwn && isLayeredCharacter(chosenKey) && !prefersReducedMotion();
  const heroFaceBg = isOwn ? null : faceBackground(chosenKey);
  // Pessoa a traço: o palco é o papel do desenho.
  const heroPaper = !isOwn && stageFraming(chosenKey) === 'portrait';
  // Símbolo antigo no palco: centralizado, com o brilho atrás.
  const heroSymbol = !isOwn && isSymbolAvatar(chosenKey);
  // Na ordem da vitrine: a Gota, que é o personagem de quem chega, vem primeiro.
  const liveFamilies = LIVE_FAMILY_IDS.flatMap((id) => FAMILIES.filter((f) => f.id === id));
  const faceFamilies = FAMILIES.filter((f) => FACE_FAMILY_IDS.includes(f.id));
  const tracoFamilies = TRACO_FAMILY_IDS.flatMap((id) => FAMILIES.filter((f) => f.id === id));
  const shownFamilies = isFaces ? faceFamilies : style === 'traco' ? tracoFamilies : liveFamilies;
  // Visual vivo: a barba do Caio cresce sozinha; aqui a pessoa escolhe como quer.
  const bearded = (BEARDED_CHARACTERS as readonly string[]).includes(chosenKey);
  const currentLook = parseLook(profileQ.data?.preferences);
  const beardNow = beardLabel(currentLook);
  // O chip marcado é o pedido desta visita ou, sem ele, o modo que já está valendo.
  const beardShown = beardAction ?? beardModeOf(currentLook);
  const dressed = (DRESSED_CHARACTERS as readonly string[]).includes(chosenKey);
  // Trajes profissionais (jaleco) só para quem tem clínica no Sec (T7.13).
  const hasClinic = useHasClinic();
  const outfits = OUTFITS.filter((outfit) => hasClinic || !CLINIC_OUTFITS.includes(outfit));
  const outfitNow = outfitChoice ?? currentLook.outfit?.current ?? 'camiseta';
  const nightNow = nightChoice ?? currentLook.outfit?.nightPajamas ?? true;
  const pronoun = /-ela\d*$/.test(chosenKey) ? 'ela' : 'ele';
  const accessory = (ACCESSORY_CHARACTERS as readonly string[]).includes(chosenKey);
  const glassesNow = glassesChoice ?? currentLook.sunglasses ?? false;
  const hairstyled = (HAIRSTYLE_CHARACTERS as readonly string[]).includes(chosenKey);
  const hairNow: Hairstyle = hairChoice ?? currentLook.hairstyle ?? 'solto-tras';
  // O Igor veste uma camisa aberta no desenho original (a "camiseta" dos outros).
  const outfitLabel = (outfit: Outfit) =>
    outfit === 'camiseta' && chosenKey === 'traco-igor' ? 'Camisa aberta' : OUTFIT_LABELS[outfit];
  useEffect(() => {
    previewGlasses(glassesChoice);
  }, [glassesChoice]);
  useEffect(() => () => previewGlasses(null), []);
  // O penteado tocado já aparece na personagem do topo; ao sair, volta ao de verdade.
  useEffect(() => {
    previewHairstyle(hairChoice);
  }, [hairChoice]);
  useEffect(() => () => previewHairstyle(null), []);
  // A roupa tocada já aparece vestida no personagem do topo; ao sair, volta a de verdade.
  useEffect(() => {
    previewOutfit(outfitChoice === null ? null : outfitIndex(outfitChoice));
  }, [outfitChoice]);
  useEffect(() => () => previewOutfit(null), []);

  /** Troca o nome sugerido junto com o personagem, se a pessoa não escreveu outro. */
  function suggestName(previous: string, next: string) {
    setName((current) => (!current.trim() || current.trim() === previous ? next : current));
  }
  function pickStyle(id: PageStyle) {
    if (id === style) return;
    // Com o personagem antigo ainda valendo, trocar de aba só mostra as opções.
    if (!keptKey) {
      if (id === 'live' && !LIVE_FAMILY_IDS.includes(familyId)) pickFamily(LIVE_FAMILY_IDS[0]!);
      else if (id === 'faces' && !FACE_FAMILY_IDS.includes(familyId))
        pickFamily(FACE_FAMILY_IDS[0]!);
      else if (id === 'traco' && !TRACO_FAMILY_IDS.includes(familyId))
        pickFamily(TRACO_FAMILY_IDS[0]!);
    }
    setStyle(id);
  }
  /** Amostra da voz: a mesma frase em cada voz, para comparar. */
  function playSample(id: string) {
    sample.current?.pause();
    if (playing === id) {
      setPlaying(null);
      return;
    }
    const audio = new Audio(`/voice/amostras/${id}.mp3`);
    sample.current = audio;
    setPlaying(id);
    audio.onended = () => setPlaying(null);
    void audio.play().catch(() => setPlaying(null));
  }
  useEffect(() => () => sample.current?.pause(), []);

  function pickFamily(id: string) {
    suggestName(defaultName, variantOf(id).name);
    setFamilyId(id);
    // Escolheu outro: o personagem antigo deixa de valer (só grava ao salvar).
    setKeptKey(null);
  }
  function toggleTrait(t: Expression) {
    setTraits((prev) =>
      prev.includes(t)
        ? prev.length > 1
          ? prev.filter((x) => x !== t)
          : prev
        : prev.length >= 3
          ? [...prev.slice(1), t]
          : [...prev, t],
    );
  }

  /** Visual novo (barba e roupa pedidas aqui), ou nada se não mudou. */
  function nextLook(): Look | null {
    let look = parseLook(profileQ.data?.preferences);
    let changed = false;
    if (beardAction && bearded) {
      look = applyBeardAction(look, beardAction, new Date());
      changed = true;
    }
    if (dressed && (outfitChoice !== null || nightChoice !== null)) {
      look = applyOutfit(look, outfitNow, nightNow);
      changed = true;
    }
    if (accessory && glassesChoice !== null) {
      look = { ...look, sunglasses: glassesChoice };
      changed = true;
    }
    if (hairstyled && hairChoice !== null) {
      look = { ...look, hairstyle: hairChoice };
      changed = true;
    }
    return changed ? look : null;
  }

  async function save() {
    try {
      await update.mutateAsync({
        characterKey: chosenKey,
        traits,
        secretaryName: name.trim() || defaultName,
        onboarded: true,
        preferences: {
          voice: chosenVoice,
          ...(nextLook() ? { [LOOK_PREF]: nextLook() } : {}),
        },
      });
      setSaved(true);
      // Primeira entrada: a primeira entrega acontece na conversa com o Secretário.
      setTimeout(() => navigate(onboarding ? '/conversas/secretario' : '/'), 700);
    } catch {
      /* o botão volta ao normal */
    }
  }

  return (
    <div className="mj">
      <div className="mj-wrap">
        <header className="mj-hd">
          {onboarding ? (
            <span />
          ) : (
            <Link href="/" className="mj-back back-btn" aria-label="Voltar para a tela inicial">
              <BackIcon />
            </Link>
          )}
        </header>

        <div ref={anchorRef} className="mj-dock-anchor" aria-hidden="true" />
        <div ref={dockRef} className="mj-dock">
          <span className="mj-dock-band" aria-hidden="true" />
          <div
            className={`mj-hero${isOwn && !heroImg ? ' own-empty' : ''}${liveHero ? ' live' : ''}${heroFaceBg ? ' is-face' : ''}${heroPaper ? ' is-paper' : ''}${heroSymbol ? ' sym' : ''}`}
            style={heroFaceBg ? ({ '--face-bg': heroFaceBg } as CSSProperties) : undefined}
          >
            {liveHero ? (
              <Suspense fallback={<img src={heroImg ?? ''} alt={name} />}>
                <div className="mj-stage">
                  <LiveStageLayers
                    key={chosenKey}
                    characterKey={chosenKey}
                    phase="listening"
                    mood={cardMood(chosenKey, dominant)}
                    levels={NO_SOUND}
                    ambient
                    say={TRAIT_SAY[dominant]}
                  />
                </div>
              </Suspense>
            ) : heroImg ? (
              <img key={heroImg} src={heroImg} alt={name} />
            ) : (
              <span className="mj-hero-wait">
                <svg viewBox="0 0 24 24" width="40" height="40" aria-hidden="true">
                  <circle
                    cx="12"
                    cy="9"
                    r="4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path
                    d="M4.5 20c1.4-3.6 4.2-5.4 7.5-5.4s6.1 1.8 7.5 5.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
                Seu avatar aparece aqui
              </span>
            )}
          </div>
        </div>

        <div className="mj-kicker">{onboarding ? 'Comece escolhendo' : 'Mudar o jeito'}</div>
        <h1 className="mj-h1">
          {onboarding ? 'Quem vai ser o seu secretário?' : 'Ajuste o personagem e a personalidade'}
        </h1>

        {keptKey && (
          <p className="mj-kept">
            <img src={characterImage(keptKey, 'simpatico')} alt="" />
            <span>
              Você usa um personagem que saiu da escolha. Ele continua com você até você escolher
              outro.
            </span>
          </p>
        )}

        <div className="mj-lbl">Estilo</div>
        <div className="mj-styles" role="tablist" aria-label="Estilo do personagem">
          {STYLE_GROUPS.map((group) => (
            <button
              key={group.id}
              type="button"
              role="tab"
              aria-selected={style === group.id}
              className={style === group.id ? 'on' : ''}
              onClick={() => pickStyle(group.id)}
            >
              <img src={characterImage(STYLE_THUMB[group.id], 'simpatico')} alt="" loading="lazy" />
              {group.label}
            </button>
          ))}
          <button
            type="button"
            role="tab"
            aria-selected={isOwn}
            className={`own${isOwn ? ' on' : ''}`}
            onClick={() => pickStyle('own')}
          >
            {ownKey ? (
              <img src={characterImage(ownKey, 'simpatico')} alt="" loading="lazy" />
            ) : (
              <span className="mj-own-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path
                    d="M4 8.5A2.5 2.5 0 0 1 6.5 6h1.7l1.3-2h5l1.3 2h1.7A2.5 2.5 0 0 1 20 8.5v8a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 16.5z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="12.5"
                    r="3.3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                </svg>
              </span>
            )}
            Crie o seu
          </button>
        </div>
        <p className="mj-style-desc">
          {isOwn
            ? 'Desenhado a partir da sua selfie, no estilo que você escolher.'
            : STYLE_GROUPS.find((g) => g.id === style)?.desc}
        </p>

        {isOwn && (
          <OwnAvatarPanel
            selected={!!ownKey && chosenKey === ownKey}
            onUse={(key) => {
              setOwnKey(key);
              // Passou a usar o avatar próprio: o personagem antigo deixa de valer.
              setKeptKey(null);
            }}
            onPreview={setOwnPreview}
            onRemoved={() => {
              setOwnKey(null);
              setStyle('live');
              void profileQ.refetch();
            }}
          />
        )}

        <div className="mj-lbl" hidden={isOwn}>
          Personagem
        </div>
        <div className="mj-row" hidden={isOwn}>
          {shownFamilies.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`mj-pick live${!keptKey && f.id === familyId ? ' on' : ''}`}
              onClick={() => pickFamily(f.id)}
            >
              <span className="mj-pod" style={{ background: f.pod }}>
                <img
                  src={characterTile(variantOf(f.id).key, 'simpatico')}
                  alt={f.label}
                  loading="lazy"
                />
              </span>
              <span className="mj-nm">{f.label}</span>
            </button>
          ))}
        </div>

        {bearded && (
          <>
            <div className="mj-lbl">
              Barba <i>· hoje: {beardNow}</i>
            </div>
            <div className="mj-chips" role="radiogroup" aria-label="Barba">
              {BEARD_CHOICES.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  role="radio"
                  aria-checked={beardShown === choice.id}
                  className={`mj-chip mj-chip-plain${beardShown === choice.id ? ' on' : ''}`}
                  onClick={() =>
                    setBeardAction(
                      // Tocar no modo que já vale não pede nada de novo.
                      beardShown === choice.id || choice.id === beardModeOf(currentLook)
                        ? null
                        : choice.id,
                    )
                  }
                >
                  {choice.label}
                </button>
              ))}
            </div>
            <p className="mj-style-desc">
              Ela cresce sozinha com os dias, até onde você deixar. Também dá para pedir para ele na
              conversa.
            </p>
          </>
        )}

        {dressed && (
          <>
            <div className="mj-lbl">
              Roupa <i>· agora: {outfitLabel(outfitNow).toLowerCase()}</i>
            </div>
            <div className="mj-chips" role="radiogroup" aria-label="Roupa">
              {outfits.map((outfit) => (
                <button
                  key={outfit}
                  type="button"
                  role="radio"
                  aria-checked={outfitNow === outfit}
                  className={`mj-chip mj-chip-plain${outfitNow === outfit ? ' on' : ''}`}
                  onClick={() => setOutfitChoice(outfit)}
                >
                  {outfitLabel(outfit)}
                </button>
              ))}
            </div>
            <div className="mj-chips" role="group" aria-label="Pijama à noite">
              <button
                type="button"
                role="switch"
                aria-checked={nightNow}
                className={`mj-chip mj-chip-plain${nightNow ? ' on' : ''}`}
                onClick={() => setNightChoice(!nightNow)}
              >
                Pijama à noite
              </button>
            </div>
            <p className="mj-style-desc">
              {nightNow ? `De camiseta, das 22h às 6h ${pronoun} aparece de pijama. ` : ''}
              Também dá para pedir na conversa: “põe a roupa de gala”.
            </p>
          </>
        )}

        {hairstyled && (
          <>
            <div className="mj-lbl">
              Cabelo <i>· agora: {HAIRSTYLE_LABELS[hairNow].toLowerCase()}</i>
            </div>
            <div className="mj-chips" role="radiogroup" aria-label="Penteado">
              {HAIRSTYLES.map((hairstyle) => (
                <button
                  key={hairstyle}
                  type="button"
                  role="radio"
                  aria-checked={hairNow === hairstyle}
                  className={`mj-chip mj-chip-plain${hairNow === hairstyle ? ' on' : ''}`}
                  onClick={() => setHairChoice(hairstyle)}
                >
                  {HAIRSTYLE_LABELS[hairstyle]}
                </button>
              ))}
            </div>
            <p className="mj-style-desc">Também dá para pedir na conversa: “solta o cabelo”.</p>
          </>
        )}

        {accessory && (
          <>
            <div className="mj-lbl">Acessório</div>
            <div className="mj-chips" role="group" aria-label="Óculos escuros">
              <button
                type="button"
                role="switch"
                aria-checked={glassesNow}
                className={`mj-chip mj-chip-plain${glassesNow ? ' on' : ''}`}
                onClick={() => setGlassesChoice(!glassesNow)}
              >
                Óculos escuros na cabeça
              </button>
            </div>
          </>
        )}

        <div className="mj-lbl">
          Personalidade <i>· até 3</i>
        </div>
        <div className="mj-chips">
          {TRAITS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`mj-chip${traits.includes(t.id) ? ' on' : ''}`}
              onClick={() => toggleTrait(t.id)}
            >
              <img src={`/styles/massinha/${t.symbol}.webp`} alt="" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="mj-lbl">
          Voz <i>· na conversa ao vivo</i>
        </div>
        <div className="mj-voices" role="radiogroup" aria-label="Voz na conversa ao vivo">
          {voiceOptions.map((option) => (
            <div key={option.id} className={`mj-voice${option.id === chosenVoice ? ' on' : ''}`}>
              <button
                type="button"
                role="radio"
                aria-checked={option.id === chosenVoice}
                className="mj-voice-pick"
                onClick={() => setVoice(option.id)}
              >
                {option.label}
              </button>
              <button
                type="button"
                className={`mj-voice-play${playing === option.id ? ' playing' : ''}`}
                onClick={() => playSample(option.id)}
                aria-label={`${playing === option.id ? 'Parar' : 'Ouvir'} a voz ${option.label.toLowerCase()}`}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                  {playing === option.id ? (
                    <path d="M7 6h3.5v12H7zM13.5 6H17v12h-3.5z" fill="currentColor" />
                  ) : (
                    <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
                  )}
                </svg>
              </button>
            </div>
          ))}
        </div>

        <div className="mj-lbl">
          {voiceGender === 'ela'
            ? 'O nome dela'
            : voiceGender === 'ele'
              ? 'O nome dele'
              : 'O nome do seu secretário'}
        </div>
        <input
          className="mj-name"
          value={name}
          maxLength={18}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex.: Sol"
        />

        {!onboarding && (
          <Link
            href="/jeito-unico"
            className={`mj-persona-card${
              stageFraming(chosenKey) === 'face'
                ? ' is-face'
                : stageFraming(chosenKey) === 'portrait'
                  ? ' is-portrait'
                  : ''
            }`}
          >
            <span className="mj-persona-pop">
              <img src={characterImage(chosenKey, 'espontaneo')} alt="" />
            </span>
            <span className="mj-persona-txt">
              <b>Deixar com a minha cara</b>
              <small>Ensine o jeito único dele →</small>
            </span>
          </Link>
        )}

        <button
          className="mj-btn"
          onClick={() => void save()}
          disabled={update.isPending || !name.trim() || (isOwn && !ownKey)}
        >
          {saved
            ? 'Salvo! ✓'
            : update.isPending
              ? 'Salvando…'
              : onboarding
                ? 'Pronto, é esse →'
                : 'Salvar mudanças'}
        </button>
      </div>
    </div>
  );
}
